import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OFFLINE_QUEUE_KEY = 'colabella_offline_queue';

// Offline queue management
const getOfflineQueue = () => {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); } catch { return []; }
};

const addToOfflineQueue = (method, url, data) => {
  const queue = getOfflineQueue();
  queue.push({ method, url, data, timestamp: new Date().toISOString() });
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

const clearOfflineQueue = () => {
  localStorage.removeItem(OFFLINE_QUEUE_KEY);
};

// Axios interceptor for offline handling
axios.interceptors.response.use(
  response => response,
  error => {
    if (!navigator.onLine || error.code === 'ERR_NETWORK' || !error.response) {
      const config = error.config;
      // Skip file uploads from offline queue (FormData can't be serialized)
      if (config && config.data instanceof FormData) {
        return Promise.reject(error);
      }
      if (config && ['post', 'put', 'delete'].includes(config.method)) {
        try {
          const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
          addToOfflineQueue(config.method, config.url?.replace(API, ''), data);
          console.log('[OFFLINE] Operazione salvata in coda locale');
          return Promise.resolve({ data: { _offline: true, message: 'Salvato offline' } });
        } catch {
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Sync offline queue when back online
const syncOfflineQueue = async () => {
  const queue = getOfflineQueue();
  if (queue.length === 0) return { synced: 0 };
  let synced = 0;
  for (const op of queue) {
    try {
      const url = op.url.startsWith('http') ? op.url : `${API}${op.url}`;
      if (op.method === 'post') await axios.post(url, op.data);
      else if (op.method === 'put') await axios.put(url, op.data);
      else if (op.method === 'delete') await axios.delete(url);
      synced++;
    } catch (e) { console.error('[SYNC] Error:', e); }
  }
  clearOfflineQueue();
  return { synced, total: queue.length };
};

// Listen for online event
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    const queue = getOfflineQueue();
    if (queue.length > 0) {
      console.log(`[ONLINE] Sincronizzazione ${queue.length} operazioni...`);
      const result = await syncOfflineQueue();
      console.log(`[ONLINE] Sincronizzate ${result.synced}/${result.total}`);
    }
  });
}

const api = {
  // Seed
  seed: () => axios.post(`${API}/seed`),
  reseed: () => axios.post(`${API}/reseed`),

  // Auth
  loginPassword: (email, password) => axios.post(`${API}/auth/login`, { email, password }).then(r => r.data),
  createUser: (data) => axios.post(`${API}/auth/create-user`, data).then(r => r.data),
  getUsers: () => axios.get(`${API}/auth/users`).then(r => r.data),
  deleteUser: (id) => axios.delete(`${API}/auth/users/${id}`).then(r => r.data),
  changePassword: (id, password) => axios.put(`${API}/auth/users/${id}/password`, { password }).then(r => r.data),

  // Assenze
  getAssenze: (workerId) => axios.get(`${API}/assenze`, { params: workerId ? { workerId } : {} }).then(r => r.data),
  createAssenza: (data) => axios.post(`${API}/assenze`, data).then(r => r.data),
  uploadAssenzaCert: (workerId, tipo, dataInizio, dataFine, note, file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/assenze/upload?workerId=${workerId}&tipo=${encodeURIComponent(tipo)}&dataInizio=${dataInizio}&dataFine=${dataFine}&note=${encodeURIComponent(note)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data); },
  deleteAssenza: (id) => axios.delete(`${API}/assenze/${id}`).then(r => r.data),
  exportPresenze: (startDate, endDate) => axios.get(`${API}/export/presenze`, { params: { start_date: startDate, end_date: endDate } }).then(r => r.data),

  // Cantieri
  getCantieri: () => axios.get(`${API}/cantieri`).then(r => r.data),
  createCantiere: (data) => axios.post(`${API}/cantieri`, data).then(r => r.data),
  updateCantiere: (id, data) => axios.put(`${API}/cantieri/${id}`, data).then(r => r.data),
  deleteCantiere: (id) => axios.delete(`${API}/cantieri/${id}`).then(r => r.data),

  // Operai
  getOperai: () => axios.get(`${API}/operai`).then(r => r.data),
  createOperaio: (data) => axios.post(`${API}/operai`, data).then(r => r.data),
  updateOperaio: (id, data) => axios.put(`${API}/operai/${id}`, data).then(r => r.data),
  deleteOperaio: (id) => axios.delete(`${API}/operai/${id}`).then(r => r.data),
  uploadFotoOperaio: (id, file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/operai/${id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data); },
  getDocumentiOperaio: (id) => axios.get(`${API}/operai/${id}/documenti`).then(r => r.data),
  uploadDocumentoOperaio: (id, tipo, numero, scadenza, note, file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/operai/${id}/documenti?tipo=${encodeURIComponent(tipo)}&numero=${encodeURIComponent(numero)}&scadenza=${scadenza}&note=${encodeURIComponent(note)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data); },
  deleteDocumentoOperaio: (id) => axios.delete(`${API}/operai/documenti/${id}`).then(r => r.data),

  // Timbrature
  getTimbrature: (date) => axios.get(`${API}/timbrature`, { params: date ? { date } : {} }).then(r => r.data),
  getTimbratureRange: (startDate, endDate) => axios.get(`${API}/timbrature`, { params: { start_date: startDate, end_date: endDate } }).then(r => r.data),
  createTimbratura: (data) => axios.post(`${API}/timbrature`, data).then(r => r.data),
  updateTimbratura: (id, data) => axios.put(`${API}/timbrature/${id}`, data).then(r => r.data),
  deleteTimbratura: (id) => axios.delete(`${API}/timbrature/${id}`).then(r => r.data),

  // Checklist
  getChecklist: () => axios.get(`${API}/checklist`).then(r => r.data),
  createChecklistItem: (data) => axios.post(`${API}/checklist`, data).then(r => r.data),
  toggleChecklistItem: (id) => axios.put(`${API}/checklist/${id}`).then(r => r.data),
  deleteChecklistItem: (id) => axios.delete(`${API}/checklist/${id}`).then(r => r.data),

  // Documenti
  getDocumenti: () => axios.get(`${API}/documenti`).then(r => r.data),
  createDocumento: (data) => axios.post(`${API}/documenti`, data).then(r => r.data),
  uploadDocumento: (name, type, category, cantiereId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ name, type, category });
    if (cantiereId) params.append('cantiereId', cantiereId);
    return axios.post(`${API}/documenti/upload?${params}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteDocumento: (id) => axios.delete(`${API}/documenti/${id}`).then(r => r.data),

  // Attivita
  getAttivita: () => axios.get(`${API}/attivita`).then(r => r.data),
  createAttivita: (data) => axios.post(`${API}/attivita`, data).then(r => r.data),
  updateAttivita: (id, data) => axios.put(`${API}/attivita/${id}`, data).then(r => r.data),
  deleteAttivita: (id) => axios.delete(`${API}/attivita/${id}`).then(r => r.data),

  // Stats
  getStats: () => axios.get(`${API}/stats`).then(r => r.data),

  // Analisi Costi
  getAnalisiCosti: (startDate, endDate) => axios.get(`${API}/analisi-costi`, { params: { start_date: startDate, end_date: endDate } }).then(r => r.data),

  // Meteo
  getMeteo: (city) => axios.get(`${API}/meteo`, { params: { city } }).then(r => r.data),

  // OCR DDT Scan
  scanDDT: (cantiereId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/ocr-ddt?cantiereId=${cantiereId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000
    }).then(r => r.data);
  },

  // Geofence
  checkGeofence: (workerId, lat, lng) => axios.post(`${API}/geofence/check`, { workerId, lat, lng }).then(r => r.data),
  getViolations: () => axios.get(`${API}/geofence/violations`).then(r => r.data),
  markViolationsRead: () => axios.put(`${API}/geofence/violations/read`).then(r => r.data),

  // Acconti
  getAcconti: (workerId) => axios.get(`${API}/acconti`, { params: workerId ? { workerId } : {} }).then(r => r.data),
  createAcconto: (data) => axios.post(`${API}/acconti`, data).then(r => r.data),
  updateAcconto: (id, data) => axios.put(`${API}/acconti/${id}`, data).then(r => r.data),
  deleteAcconto: (id) => axios.delete(`${API}/acconti/${id}`).then(r => r.data),

  // Magazzino
  getMagazzino: (type) => axios.get(`${API}/magazzino`, { params: type ? { type } : {} }).then(r => r.data),
  createMagazzinoItem: (data) => axios.post(`${API}/magazzino`, data).then(r => r.data),
  updateMagazzinoItem: (id, data) => axios.put(`${API}/magazzino/${id}`, data).then(r => r.data),
  deleteMagazzinoItem: (id) => axios.delete(`${API}/magazzino/${id}`).then(r => r.data),
  movimentoMateriale: (data) => axios.post(`${API}/magazzino/movimento`, data).then(r => r.data),
  getCantiereMateriali: (cantiereId) => axios.get(`${API}/cantiere-materiali`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  getMovimentiMateriale: (itemId) => axios.get(`${API}/movimenti-materiale`, { params: itemId ? { itemId } : {} }).then(r => r.data),
  importExcelMagazzino: (typeImport, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/magazzino/import-excel?type_import=${typeImport}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },

  // Log Mezzi
  getMezziLog: (mezzoId) => axios.get(`${API}/mezzi-log`, { params: mezzoId ? { mezzoId } : {} }).then(r => r.data),
  createMezzoLog: (data) => axios.post(`${API}/mezzi-log`, data).then(r => r.data),
  updateMezzoLog: (id, data) => axios.put(`${API}/mezzi-log/${id}`, data).then(r => r.data),
  deleteMezzoLog: (id) => axios.delete(`${API}/mezzi-log/${id}`).then(r => r.data),

  // Confronta prezzi
  confrontaPrezzi: (nome) => axios.get(`${API}/magazzino/confronta`, { params: { nome } }).then(r => r.data),

  // Pagamenti Ricevuti
  getPagamenti: (cantiereId) => axios.get(`${API}/pagamenti`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  createPagamento: (data) => axios.post(`${API}/pagamenti`, data).then(r => r.data),
  updatePagamento: (id, data) => axios.put(`${API}/pagamenti/${id}`, data).then(r => r.data),
  deletePagamento: (id) => axios.delete(`${API}/pagamenti/${id}`).then(r => r.data),

  // Appuntamenti
  getAppuntamenti: (cantiereId) => axios.get(`${API}/appuntamenti`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  createAppuntamento: (data) => axios.post(`${API}/appuntamenti`, data).then(r => r.data),
  updateAppuntamento: (id, data) => axios.put(`${API}/appuntamenti/${id}`, data).then(r => r.data),
  deleteAppuntamento: (id) => axios.delete(`${API}/appuntamenti/${id}`).then(r => r.data),

  // Preventivi e Fatture
  getPrevFatture: (cantiereId) => axios.get(`${API}/prev-fatture`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  createPrevFattura: (data) => axios.post(`${API}/prev-fatture`, data).then(r => r.data),
  updatePrevFattura: (id, data) => axios.put(`${API}/prev-fatture/${id}`, data).then(r => r.data),
  deletePrevFattura: (id) => axios.delete(`${API}/prev-fatture/${id}`).then(r => r.data),

  // Giornale Lavori
  getGiornale: (cantiereId, date) => axios.get(`${API}/giornale`, { params: { ...(cantiereId && { cantiereId }), ...(date && { date }) } }).then(r => r.data),
  createGiornale: (data) => axios.post(`${API}/giornale`, data).then(r => r.data),
  createGiornaleConScarico: (data) => axios.post(`${API}/giornale/con-scarico`, data).then(r => r.data),
  updateGiornale: (id, data) => axios.put(`${API}/giornale/${id}`, data).then(r => r.data),
  deleteGiornale: (id) => axios.delete(`${API}/giornale/${id}`).then(r => r.data),

  // Foto Cantiere
  getFoto: (cantiereId) => axios.get(`${API}/foto`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  uploadFoto: (cantiereId, file, description, date) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/foto?cantiereId=${cantiereId}&description=${encodeURIComponent(description || '')}&date=${date || ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  deleteFoto: (id) => axios.delete(`${API}/foto/${id}`).then(r => r.data),
  getUploadUrl: (filename) => `${BACKEND_URL}/api/uploads/${filename}`,

  // Documenti Fiscali
  getDocFiscali: (cantiereId) => axios.get(`${API}/documenti-fiscali`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  uploadDocFiscale: (cantiereId, type, number, supplier, description, date, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ cantiereId, type, number: number || '', supplier: supplier || '', description: description || '', date: date || '' });
    return axios.post(`${API}/documenti-fiscali/upload?${params}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  deleteDocFiscale: (id) => axios.delete(`${API}/documenti-fiscali/${id}`).then(r => r.data),

  // Backup & Sync
  getBackup: () => axios.get(`${API}/backup`).then(r => r.data),
  restoreBackup: (data) => axios.post(`${API}/restore`, data).then(r => r.data),
  syncOffline: syncOfflineQueue,
  getOfflineQueueCount: () => getOfflineQueue().length,

  // Fatture Fornitori
  getFattureForni: (status) => axios.get(`${API}/fatture-fornitori`, { params: status ? { status } : {} }).then(r => r.data),
  createFatturaForni: (data) => axios.post(`${API}/fatture-fornitori`, data).then(r => r.data),
  createFatturaConMateriali: (data) => axios.post(`${API}/fatture-fornitori/con-materiali`, data).then(r => r.data),
  uploadFatturaForni: (supplier, number, amount, date, dueDate, description, cantiereId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const p = new URLSearchParams({ supplier, number, amount: String(amount), date, dueDate, description });
    if (cantiereId) p.append('cantiereId', cantiereId);
    return axios.post(`${API}/fatture-fornitori/upload?${p}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  updateFatturaForni: (id, data) => axios.put(`${API}/fatture-fornitori/${id}`, data).then(r => r.data),
  updateFatturaMateriali: (id, materiali) => axios.put(`${API}/fatture-fornitori/${id}/materiali`, { materiali }).then(r => r.data),
  addPagamentoParziale: (id, data) => axios.post(`${API}/fatture-fornitori/${id}/pagamento-parziale`, data).then(r => r.data),
  deleteFatturaForni: (id) => axios.delete(`${API}/fatture-fornitori/${id}`).then(r => r.data),

  // Appuntamenti
  getAppuntamenti: () => axios.get(`${API}/appuntamenti`).then(r => r.data),
  createAppuntamento: (data) => axios.post(`${API}/appuntamenti`, data).then(r => r.data),
  updateAppuntamento: (id, data) => axios.put(`${API}/appuntamenti/${id}`, data).then(r => r.data),
  deleteAppuntamento: (id) => axios.delete(`${API}/appuntamenti/${id}`).then(r => r.data),

  // Computo Metrico + SAL
  getComputo: (cantiereId) => axios.get(`${API}/computo`, { params: cantiereId ? { cantiereId } : {} }).then(r => r.data),
  createVoceComputo: (data) => axios.post(`${API}/computo`, data).then(r => r.data),
  updateVoceComputo: (id, data) => axios.put(`${API}/computo/${id}`, data).then(r => r.data),
  deleteVoceComputo: (id) => axios.delete(`${API}/computo/${id}`).then(r => r.data),
  importComputoScan: (cantiereId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/computo/import-scan?cantiereId=${cantiereId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 }).then(r => r.data);
  },
  importComputoExcel: (cantiereId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/computo/import-excel?cantiereId=${cantiereId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  getMisure: (cantiereId, voceId) => axios.get(`${API}/misure`, { params: { ...(cantiereId && { cantiereId }), ...(voceId && { voceId }) } }).then(r => r.data),
  createMisura: (data) => axios.post(`${API}/misure`, data).then(r => r.data),
  deleteMisura: (id) => axios.delete(`${API}/misure/${id}`).then(r => r.data),
  getSAL: (cantiereId) => axios.get(`${API}/sal/${cantiereId}`).then(r => r.data),
};

export default api;
